/**
 * ADJUDON Product Analytics Service (#50)
 *
 * PostHog-backed analytics with:
 * - EU data residency (eu.posthog.com)
 * - GDPR consent gating — no tracking until user opts in
 * - Dev-mode console fallback when VITE_POSTHOG_KEY is unset
 * - Type-safe event taxonomy
 * - Auto page-view tracking disabled (we fire manually per SPA route)
 *
 * Cardinal Rules enforced:
 * - Rule #3: EU host only (eu.posthog.com)
 * - Rule #4: No raw trace data in analytics events
 * - Rule #6: No customer data sent to external ML/training
 */

import posthog from 'posthog-js';
import type {
  AnalyticsEventName,
  AnalyticsEventProperties,
  AnalyticsConsentStatus,
  AnalyticsUserTraits,
} from '../types/api';

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST as string | undefined ?? 'https://eu.i.posthog.com';
const IS_DEV = import.meta.env.DEV;

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let initialized = false;
let consentStatus: AnalyticsConsentStatus = 'pending';

// ---------------------------------------------------------------------------
// Initialization
// ---------------------------------------------------------------------------

/**
 * Initialize PostHog. Safe to call multiple times — no-op after first init.
 * Call this once in main.tsx AFTER Sentry init.
 */
export function initAnalytics(): void {
  if (initialized) return;

  if (!POSTHOG_KEY) {
    if (IS_DEV) {
      console.info('[analytics] No VITE_POSTHOG_KEY — using console fallback');
    }
    initialized = true;
    return;
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,

    // GDPR: start opted-out, opt in only after explicit consent
    opt_out_capturing_by_default: true,

    // SPA — we handle page views manually via trackPageView()
    capture_pageview: false,
    capture_pageleave: true,

    // Session recording — opt-in only, never auto
    disable_session_recording: true,

    // No autocapture — we use explicit events for clean taxonomy
    autocapture: false,

    // Persistence via localStorage (not cookies — simpler GDPR story)
    persistence: 'localStorage',

    // EU data residency — never route through US
    request_batching: true,

    // Don't send $ip to PostHog (GDPR minimization)
    ip: false,

    loaded: () => {
      if (IS_DEV) {
        console.info('[analytics] PostHog initialized (EU host)');
      }
    },
  });

  initialized = true;
}

// ---------------------------------------------------------------------------
// Consent Management (GDPR)
// ---------------------------------------------------------------------------

/**
 * Grant analytics consent. Call when user accepts cookie/analytics banner.
 * Until this is called, NO events are sent to PostHog.
 */
export function grantConsent(): void {
  consentStatus = 'granted';
  if (POSTHOG_KEY && initialized) {
    posthog.opt_in_capturing();
  }
}

/**
 * Deny analytics consent. Stops all tracking and clears local data.
 */
export function denyConsent(): void {
  consentStatus = 'denied';
  if (POSTHOG_KEY && initialized) {
    posthog.opt_out_capturing();
  }
}

/** Get current consent status */
export function getConsentStatus(): AnalyticsConsentStatus {
  return consentStatus;
}

// ---------------------------------------------------------------------------
// User Identification
// ---------------------------------------------------------------------------

/**
 * Identify the current user after login.
 * Traits are PII-minimal: no trace data, no passwords, no tokens.
 */
export function identifyUser(userId: string, traits?: AnalyticsUserTraits): void {
  if (!initialized) return;

  if (!POSTHOG_KEY) {
    if (IS_DEV) {
      console.info('[analytics] identify', userId, traits);
    }
    return;
  }

  posthog.identify(userId, traits ? { ...traits } : undefined);
}

/**
 * Reset identity on logout. Generates a new anonymous ID.
 */
export function resetUser(): void {
  if (!initialized) return;

  if (!POSTHOG_KEY) {
    if (IS_DEV) {
      console.info('[analytics] reset');
    }
    return;
  }

  posthog.reset();
}

// ---------------------------------------------------------------------------
// Event Tracking
// ---------------------------------------------------------------------------

/**
 * Track a product analytics event.
 *
 * IMPORTANT: Never include raw trace payloads, API keys, or PII in properties.
 * Only structured, known fields from AnalyticsEventProperties.
 */
export function trackEvent(
  event: AnalyticsEventName,
  properties?: AnalyticsEventProperties,
): void {
  if (!initialized) return;

  // Clean properties — strip undefined values
  const clean = properties
    ? Object.fromEntries(
        Object.entries(properties).filter(([, v]) => v !== undefined),
      )
    : undefined;

  if (!POSTHOG_KEY) {
    if (IS_DEV) {
      console.info('[analytics] track', event, clean);
    }
    return;
  }

  posthog.capture(event, clean);
}

/**
 * Track a page view. Call on every SPA route change.
 */
export function trackPageView(path: string, title?: string): void {
  trackEvent('page_viewed', {
    page: path,
    ...(title ? { component: title } : {}),
  });
}

/**
 * Track feature usage (generic catch-all for UI interactions).
 */
export function trackFeature(feature: string, properties?: AnalyticsEventProperties): void {
  trackEvent('feature_used', {
    action: feature,
    ...properties,
  });
}

// ---------------------------------------------------------------------------
// Group / Organization
// ---------------------------------------------------------------------------

/**
 * Associate the current user with their organization (PostHog group analytics).
 */
export function setOrganization(orgId: string, orgName?: string, plan?: string): void {
  if (!initialized) return;

  if (!POSTHOG_KEY) {
    if (IS_DEV) {
      console.info('[analytics] group', orgId, orgName, plan);
    }
    return;
  }

  posthog.group('organization', orgId, {
    name: orgName,
    plan,
  });
}

// ---------------------------------------------------------------------------
// Feature Flags (PostHog A/B testing)
// ---------------------------------------------------------------------------

/**
 * Check if a PostHog feature flag is enabled.
 * Returns false if PostHog is not initialized.
 */
export function isFeatureEnabled(flag: string): boolean {
  if (!POSTHOG_KEY || !initialized) return false;
  return posthog.isFeatureEnabled(flag) ?? false;
}

/**
 * Get a feature flag value (multivariate flags).
 */
export function getFeatureFlag(flag: string): string | boolean | undefined {
  if (!POSTHOG_KEY || !initialized) return undefined;
  return posthog.getFeatureFlagPayload(flag) as string | boolean | undefined;
}

// ---------------------------------------------------------------------------
// Cleanup
// ---------------------------------------------------------------------------

/**
 * Shut down PostHog cleanly. Call on app unmount if needed.
 */
export function shutdownAnalytics(): void {
  if (!POSTHOG_KEY || !initialized) return;
  // Flush pending events
  posthog.capture('$pageleave');
}
